/**
 * Admin-app "view as role" preview: UI/navigation only; JWT and API stay adminapp.
 */
import type { UserRole } from '../../types';
import { hasRoleToken, isAdminAppAccount } from './roleTokens';

export const VIEW_AS_STORAGE_KEY = 'logistrade_view_as_role';

/** Roles an adminapp account may preview (adminapp itself is excluded). */
export const PREVIEWABLE_ROLES: readonly UserRole[] = [
  'admin',
  'ceo',
  'accounting',
  'sales_rep',
  'operations',
  'client',
];

export function canUseViewAs(roles: unknown): boolean {
  return isAdminAppAccount(roles);
}

export function isPreviewableRole(role: string): role is UserRole {
  return (PREVIEWABLE_ROLES as readonly string[]).includes(role);
}

export function getEffectiveRoles(realRoles: unknown, viewAsRole: UserRole | null): UserRole[] {
  if (viewAsRole) return [viewAsRole];
  if (Array.isArray(realRoles)) return realRoles.map((r) => String(r) as UserRole);
  return [];
}

export function hasEffectiveRole(
  realRoles: unknown,
  viewAsRole: UserRole | null,
  wanted: UserRole | UserRole[],
): boolean {
  const list = Array.isArray(wanted) ? wanted : [wanted];
  if (viewAsRole) {
    return list.some((r) => hasRoleToken([viewAsRole], r));
  }
  return list.some((r) => hasRoleToken(realRoles, r)) || hasRoleToken(realRoles, 'adminapp');
}

export function loadViewAsRole(): UserRole | null {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
    if (!raw || !isPreviewableRole(raw)) return null;
    return raw;
  } catch {
    return null;
  }
}

export function saveViewAsRole(role: UserRole | null): void {
  try {
    if (!role) sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
    else sessionStorage.setItem(VIEW_AS_STORAGE_KEY, role);
  } catch {
    /* sessionStorage unavailable */
  }
}
